import { BidderRule } from '@pubstack/common/src/bidder-rule'
import { positiveIntegerRegExp } from '@pubstack/common/src/input'
import { FunctionComponent, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Toggle } from '~/components/Toggle'
import { StackBidderRuleFlyout } from './StackBidderRuleFlyout'
import { StackFormData } from './StackEdit'
import { useNavigate } from 'react-router-dom'
import { useScopePermissions } from '~/auth/useScopePermissions'

const TITLE_CLASSES = 'font-rubik text-base/[26px] font-medium text-jet'
const SUBTITLE_CLASSES = 'mb-5 font-roboto text-base/[26px] text-jet'

type EditBidderRuleConfirmModalProps = {
  onConfirm: () => void
}

const EditBidderRuleConfirmModal: FunctionComponent<EditBidderRuleConfirmModalProps> = ({ onConfirm }) => {
  const { close } = useGlobalModal()
  return (
    <Modal.Content>
      <Modal.Title>Leaving the page</Modal.Title>

      <Modal.Body>
        <div>
          <p>You are about to leave this page. All stack changes will be lost.</p>
          <p>Do you confirm ?</p>
        </div>
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'adm-sites-stacks-form-edit-header-bidding-rules-cancel'}>
          Cancel
        </Button>
        <Button variant={'primary'} onClick={onConfirm} trackingLabel={'adm-sites-stacks-form-edit-header-bidding-rules-confirm'}>
          Confirm
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

type PureStackHeaderBiddingRulesStepProps = {
  selectableHeaderBiddingRules: BidderRule[]
}
export const PureStackHeaderBiddingRulesStep: FunctionComponent<PureStackHeaderBiddingRulesStepProps> = ({ selectableHeaderBiddingRules }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<StackFormData>()
  const biddersSelectOptions: SelectOptionProp<string>[] = selectableHeaderBiddingRules.map(
    (l) => ({ label: l.name, value: l.id, trackingLabel: 'adm-sites-stack-form-bidders-set' }) as SelectOptionProp<string>
  )

  const bidderRuleId = useWatch({
    control,
    name: 'configuration.bidderRuleId',
  })
  const [ruleDetail, setRuleDetail] = useState<BidderRule | undefined>(() => selectableHeaderBiddingRules.find((hb) => hb.id === bidderRuleId))
  const modal = useGlobalModal()
  const flyout = useGlobalFlyout()
  const navigate = useNavigate()

  const [previousBidderRuleId, setPreviousBidderRuleId] = useState(bidderRuleId)
  const { canEdit } = useScopePermissions()

  // the selected rule follows the form value: realign on change, during render rather than in an effect
  if (previousBidderRuleId !== bidderRuleId) {
    setPreviousBidderRuleId(bidderRuleId)
    const hbRule = selectableHeaderBiddingRules.find((hb) => hb.id === bidderRuleId)
    if (hbRule) {
      setRuleDetail(hbRule)
    }
  }

  return (
    <div>
      <h2 className={TITLE_CLASSES}>Header bidding</h2>
      <div className={SUBTITLE_CLASSES}>Define header bidding settings.</div> {/* Todo: update this subtitle */}
      <div className={'flex flex-col gap-5'}>
        <Controller
          control={control}
          name={'configuration.headerBiddingEnabled'}
          render={({ field: { value: headerBiddingEnabled, onChange } }) => {
            return (
              <>
                <div className={'mb-1 flex flex-row gap-2'}>
                  <Toggle
                    value={!!headerBiddingEnabled}
                    onClick={() => {
                      onChange(!headerBiddingEnabled)
                    }}
                    trackingLabel={headerBiddingEnabled ? 'adm-sites-stacks-form-header-bidding-unset' : 'adm-sites-stacks-form-header-bidding-set'}
                  />
                  <div className={SUBTITLE_CLASSES}>Header bidding</div>
                </div>
                {!!headerBiddingEnabled && (
                  <>
                    <Input
                      type={'number'}
                      control={control}
                      name={'configuration.adCallTimeout'}
                      label={'Timeout'}
                      iconRight={'millisecond'}
                      error={errors.configuration?.adCallTimeout?.message}
                      min={0}
                      rules={{
                        required: 'Timeout required',
                        pattern: { value: positiveIntegerRegExp, message: 'Incorrect value' },
                      }}
                      isMessageBlock={true}
                      className={'w-[464px]'}
                    />

                    <div className={'flex flex-row gap-2'}>
                      <div className={'flex w-[464px] flex-col gap-4'}>
                        <Select type={'select'} control={control} name={'configuration.bidderRuleId'} label={'Bidder rule'} options={biddersSelectOptions} />
                      </div>
                      {ruleDetail && (
                        <Button
                          variant="tertiary"
                          onClick={() =>
                            flyout.open(
                              StackBidderRuleFlyout,
                              { color: Colors.Forest, width: `816px`, closeTrackingLabel: 'adm-sites-stacks-form-header-bidding-detail-cancel' },
                              {
                                bidderRule: ruleDetail,
                                onEdit: () => {
                                  modal.open(EditBidderRuleConfirmModal, {
                                    onConfirm: () => {
                                      navigate(`/monetization/inventory/bidder-rules/edit/${ruleDetail.id}`)
                                      modal.close()
                                      flyout.close()
                                    },
                                  })
                                },
                                /* The default rule is editable like any other; what still gates the button
                                   is the permission to save one. */
                                isEditable: canEdit,
                              }
                            )
                          }
                          trackingLabel={'adm-sites-stacks-form-bidder-rule-details-view'}
                        >
                          See rule details
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </>
            )
          }}
        />
      </div>
    </div>
  )
}
